import { useTranslation } from "react-i18next";

import { FormikProps } from "formik";

import { AssetDocumentArea, PendingDocumentMove } from "../../../../../api/ClientFileApi";
import { IncomeSectionCard } from "../../../income/components/income-section-card/IncomeSectionCard";
import { AssetsFormValues } from "../../AssetsView.types";
import { AssetsDocumentArea } from "../../AssetsView.utils";
import { AssetUploadArea } from "../asset-upload-area/AssetUploadArea";
import { AssetYesNoQuestion } from "../asset-yes-no-question/AssetYesNoQuestion";

const SECTION = "clients.client.debtSettlement.assetInventory.assetsTab.movables";

interface Props {
  clientId: string;
  formik: FormikProps<AssetsFormValues>;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

export function MovableGoodsSection({ clientId, formik, onAddMove, onCancelMove }: Props) {
  const { t } = useTranslation();

  const uploadArea = (area: AssetsDocumentArea, label: string) => (
    <AssetUploadArea
      clientId={clientId}
      formik={formik}
      area={area}
      label={label}
      onAddMove={onAddMove}
      onCancelMove={onCancelMove}
    />
  );

  return (
    <IncomeSectionCard title={t(`${SECTION}.title`)}>
      <AssetYesNoQuestion
        formik={formik}
        field="hasValuables"
        label={t(`${SECTION}.hasValuables`)}
        detailsField="valuablesDetails"
        detailsLabel={t(`${SECTION}.valuablesDetails`)}
      />
      <AssetYesNoQuestion
        formik={formik}
        field="hasFurnishings"
        label={t(`${SECTION}.hasFurnishings`)}
        detailsField="furnishingsDetails"
        detailsLabel={t(`${SECTION}.furnishingsDetails`)}
        detailsInfo={t(`${SECTION}.furnishingsInfo`)}
      />
      <AssetYesNoQuestion
        formik={formik}
        field="hasAppliances"
        label={t(`${SECTION}.hasAppliances`)}
        detailsField="appliancesDetails"
        detailsLabel={t(`${SECTION}.appliancesDetails`)}
        detailsInfo={t(`${SECTION}.appliancesInfo`)}
      />
      <AssetYesNoQuestion
        formik={formik}
        field="hasEquipment"
        label={t(`${SECTION}.hasEquipment`)}
        detailsField="equipmentDetails"
        detailsLabel={t(`${SECTION}.equipmentDetails`)}
        detailsInfo={t(`${SECTION}.equipmentInfo`)}
      />
      <AssetYesNoQuestion
        formik={formik}
        field="hasVehicles"
        label={t(`${SECTION}.hasVehicles`)}
        detailsField="vehiclesDetails"
        detailsLabel={t(`${SECTION}.vehiclesDetails`)}
      >
        {uploadArea(AssetDocumentArea.Vehicles, t(`${SECTION}.vehiclesUpload`))}
      </AssetYesNoQuestion>
      <AssetYesNoQuestion
        formik={formik}
        field="hasBuildingsOnForeignLand"
        label={t(`${SECTION}.hasBuildingsOnForeignLand`)}
        detailsField="buildingsOnForeignLandDetails"
        detailsLabel={t(`${SECTION}.buildingsOnForeignLandDetails`)}
      >
        {uploadArea(AssetDocumentArea.BuildingsOnForeignLand, t(`${SECTION}.buildingsOnForeignLandUpload`))}
      </AssetYesNoQuestion>
      <AssetYesNoQuestion
        formik={formik}
        field="hasOtherItems"
        label={t(`${SECTION}.hasOtherItems`)}
        detailsField="otherItemsDetails"
        detailsLabel={t(`${SECTION}.otherItemsDetails`)}
      />
      <AssetYesNoQuestion
        formik={formik}
        field="ownsAllItems"
        label={t(`${SECTION}.ownsAllItems`)}
        detailsField="itemsOwnedByOthersDetails"
        detailsLabel={t(`${SECTION}.itemsOwnedByOthersDetails`)}
        detailsTrigger={false}
      />
    </IncomeSectionCard>
  );
}
